<template>
  <div class="cd-detail">
    <b-row class="mb-5">
      <b-col cols="4">
        <b-input
          v-model="employeeId"
          size="sm"
          placeholder="employeeId (UUID nhân viên)"
          autocomplete="off"
        />
      </b-col>
      <b-col cols="2">
        <b-input
          v-model="cycle"
          size="sm"
          placeholder="Kỳ (YYYY-MM)"
          autocomplete="off"
        />
      </b-col>
      <b-col cols="2">
        <b-form-select
          v-model="status"
          :options="statusOptions"
          size="sm"
          class="select-style"
        ></b-form-select>
      </b-col>
      <b-col cols="1">
        <b-button
          size="sm"
          variant="primary"
          style="font-weight: 600; width: 70px"
          :disabled="!employeeId || !cycle"
          @click="reload"
        >
          Tải
        </b-button>
      </b-col>
    </b-row>

    <b-table
      :items="items"
      :fields="fields"
      :small="true"
      :busy="loading"
      responsive
      bordered
      hover
      show-empty
      empty-text="Không có dữ liệu — nhập employeeId + kỳ rồi bấm Tải"
    >
      <template #table-busy>
        <div class="text-center text-primary my-3">
          <b-spinner small class="align-middle mr-2"></b-spinner>
          <strong>Đang tải...</strong>
        </div>
      </template>

      <template #cell(amount)="data">
        {{ format(data.item.amount) }}
      </template>

      <template #cell(status)="data">
        <span :class="statusClass(data.item.status)">
          {{ data.item.status }}
        </span>
      </template>

      <template #cell(createdAt)="data">
        {{ formatDate(data.item.createdAt) }}
      </template>

      <template #cell(actions)="data">
        <b-button
          v-if="data.item.status === 'PENDING' && canWaive"
          size="sm"
          variant="warning"
          @click="openWaive(data.item)"
        >
          <i class="fas fa-ban cd-btn-icon"></i> Miễn nợ
        </b-button>
      </template>
    </b-table>

    <b-row>
      <b-col>
        <p class="mt-3 text-dark" style="font-weight: 500">
          Tổng số: {{ total }}
        </p>
      </b-col>
      <b-col>
        <b-pagination-nav
          v-if="numberOfPage >= 2"
          class="custom-pagination"
          :link-gen="linkGen"
          :number-of-pages="numberOfPage"
          v-model="options.page"
          @change="onPageChange"
          align="right"
          first-class="page-item-first btn btn-icon mr-1 btn-sm my-1"
          prev-class="page-item-prev btn btn-icon mr-1 btn-sm my-1"
          next-class="page-item-next btn btn-icon btn-sm mr-1 my-1"
          last-class="page-item-last btn btn-icon btn-sm my-1"
          page-class="btn btn-icon btn-sm border-0 mr-1 my-1"
        >
          <template v-slot:first-text>
            <i class="ki ki-double-arrow-back icon-xs"></i>
          </template>
          <template v-slot:prev-text>
            <i class="ki ki-arrow-back icon-xs"></i>
          </template>
          <template v-slot:next-text>
            <i class="ki ki-arrow-next icon-xs"></i>
          </template>
          <template v-slot:last-text>
            <i class="ki ki-double-arrow-next icon-xs"></i>
          </template>
        </b-pagination-nav>
      </b-col>
    </b-row>

    <WaiveDebtDialog
      v-model="waiveDialog"
      :debt-id="selected ? Number(selected.id) : 0"
      :amount="selected ? selected.amount : 0"
      :voucher-code="selected ? selected.voucherCode : ''"
      @waived="onWaived"
    />
  </div>
</template>

<script>
import { CommissionDebtService } from '@/core/services/commission-debt.service';
import WaiveDebtDialog from './WaiveDebtDialog.vue';

export default {
  name: 'DetailTab',
  components: { WaiveDebtDialog },
  props: {
    prefill: { type: Object, default: null },
  },
  data() {
    return {
      employeeId: '',
      cycle: '',
      status: null,
      statusOptions: [
        { value: null, text: 'Tất cả trạng thái' },
        { value: 'PENDING', text: 'PENDING' },
        { value: 'SETTLED', text: 'SETTLED' },
        { value: 'WAIVED', text: 'WAIVED' },
      ],
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 20 },
      loading: false,
      waiveDialog: false,
      selected: null,
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
      fields: [
        {
          key: 'contractNumber',
          label: 'Số hợp đồng',
          thClass: 'text-center',
        },
        {
          key: 'voucherCode',
          label: 'Tên voucher',
          thClass: 'text-center',
        },
        {
          key: 'employeeRole',
          label: 'Vai trò',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'amount',
          label: 'Số tiền',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '12%' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'createdAt',
          label: 'Ngày áp voucher',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '14%' },
        },
        {
          key: 'actions',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '10%' },
        },
      ],
    };
  },
  computed: {
    canWaive() {
      const perms = this.$store?.state?.auth?.permissions || [];
      return perms.includes('COMMISSION_DEBT_WAIVE');
    },
    numberOfPage() {
      return Math.ceil(this.total / this.options.itemsPerPage);
    },
  },
  watch: {
    prefill: {
      immediate: true,
      handler(v) {
        if (v) {
          this.employeeId = v.employeeId;
          this.cycle = v.cycle;
          this.options.page = 1;
          this.reload();
        }
      },
    },
  },
  methods: {
    async reload() {
      if (!this.employeeId || !this.cycle) return;
      this.loading = true;
      try {
        const res = await CommissionDebtService.list({
          employeeId: this.employeeId,
          cycle: this.cycle,
          status: this.status || undefined,
          page: this.options.page,
          limit: this.options.itemsPerPage,
        });
        // BE wrap qua ResponseInterceptor → res.data.data.data
        const payload = res?.data?.data?.data || {};
        this.items = payload.items || [];
        this.total = payload.total || 0;
      } catch (e) {
        this.items = [];
        this.total = 0;
        const status = e?.response?.status;
        const msg =
          e?.response?.data?.message || e?.message || 'Lỗi không xác định';
        // eslint-disable-next-line no-console
        console.error('[DetailTab] reload error:', status, msg, e);
        this.$bvToast?.toast?.(`API lỗi (${status || '?'}): ${msg}`, {
          variant: 'danger',
          title: 'Không tải được dữ liệu',
          autoHideDelay: 8000,
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.options.page = page;
      this.reload();
    },
    format(n) {
      return Number(n || 0).toLocaleString('vi-VN');
    },
    formatDate(s) {
      if (!s) return '';
      const d = new Date(s);
      return d.toLocaleString('vi-VN', { hour12: false });
    },
    statusClass(s) {
      if (s === 'PENDING') return 'cd-badge cd-badge--pending';
      if (s === 'SETTLED') return 'cd-badge cd-badge--settled';
      if (s === 'WAIVED') return 'cd-badge cd-badge--waived';
      return 'cd-badge';
    },
    openWaive(item) {
      this.selected = item;
      this.waiveDialog = true;
    },
    onWaived() {
      this.waiveDialog = false;
      this.reload();
    },
  },
};
</script>

<style lang="scss" scoped>
.cd-detail {
  .cd-btn-icon {
    color: inherit !important;
    font-size: 11px;
    margin-right: 3px;
  }
  .cd-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 100px;
    font-size: 11.5px;
    font-weight: 600;
    &--pending {
      background: #fff4de;
      color: #ffa800;
    }
    &--settled {
      background: #c9f7f5;
      color: #1bc5bd;
    }
    &--waived {
      background: #ecf1f6;
      color: #7e8299;
    }
  }
}
</style>
